import { useEffect, useRef, useState } from "react";
import {
    alpha,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { ArrowBack, ArrowForward, AutoAwesome, RestartAlt, Send, Visibility } from "@mui/icons-material";
import clsx from "clsx";
import { makeStyles } from "@/compat/muiStyles";
import { useHistory } from "@/compat/router";
import { useSnackbar } from "notistack";
import CardBase from "../../../components/cards/CardBase";
import AiAssessmentQuickView from "../../components/riskassessments/AiAssessmentQuickView";
import RiskAssessmentNextStepsCard from "../../components/riskassessments/RiskAssessmentNextStepsCard";
import AiAssessmentReview from "../../components/riskassessments/AiAssessmentReview";
import useSendRiskAssessmentAiMessage from "../../hooks/mutations/useSendRiskAssessmentAiMessage";
import useAddTemplate from "../../hooks/mutations/useAddTemplate";
import usePeople from "../../hooks/queries/usePeople";
import { buildAiTemplatePayload } from "../../utils/aiTemplatePayload";
import { useAppSelector } from "@/redux/hooks";
import { useAuth } from "../../../contexts/authContext";
import moduleAreas, { moduleUrls } from "../../constants/moduleAreaConstants";
import { baseRoute } from "../../routes";
import type { RiskAssessmentAiAssessmentDTO, RiskAssessmentAiReplyDTO } from "../../types/riskAssessmentAiDtos";

const useStyles = makeStyles((theme) => ({
    layout: { display: "flex", gap: theme.spacing(2), alignItems: "flex-start" },
    chatColumn: { flex: 1, minWidth: 0 },
    sideColumn: { width: 320, flexShrink: 0, display: "flex", flexDirection: "column", gap: theme.spacing(2) },
    chatHeader: {
        alignItems: "center",
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
    },
    chatHeaderSpacer: { flex: 1 },
    working: { height: 3 },
    transcript: { display: "flex", flexDirection: "column", gap: theme.spacing(2), minHeight: 380, padding: theme.spacing(2) },
    assistantRow: { display: "flex", gap: theme.spacing(1.5) },
    userRow: { display: "flex", justifyContent: "flex-end" },
    systemRow: {
        backgroundColor: alpha(theme.palette.warning.main, 0.08),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(1, 1.5),
    },
    userBubble: {
        backgroundColor: theme.palette.action.hover,
        borderRadius: theme.shape.borderRadius,
        maxWidth: "70%",
        padding: theme.spacing(1.5, 2),
        whiteSpace: "pre-wrap",
    },
    questionList: { margin: theme.spacing(1, 0, 0), paddingLeft: theme.spacing(2.5) },
    time: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(0.5) },
    timeRight: { textAlign: "right" },
    composer: { borderTop: `1px solid ${theme.palette.divider}`, padding: theme.spacing(2) },
    composerActions: { display: "flex", justifyContent: "flex-end", marginTop: theme.spacing(1) },
    disclaimer: { color: theme.palette.text.secondary, display: "block", marginTop: theme.spacing(1) },
    card: { padding: theme.spacing(2) },
    progressLabel: { display: "flex", justifyContent: "space-between", marginBottom: theme.spacing(0.5) },
    summaryItem: { color: theme.palette.text.secondary },
    hazard: { marginTop: theme.spacing(1.5) },
    footer: { display: "flex", justifyContent: "space-between", marginTop: theme.spacing(2) },
}));

interface Turn {
    role: "user" | "assistant" | "system";
    text: string | null;
    questions?: string[] | null;
    at: Date;
}

// Mirrors ErrorMessages.RiskAssessmentAi on the server: these two mean the
// conversation is over for good, not that the message should be retried.
const terminalErrors = [
    "This conversation has reached its limit",
    "The assistant can only help build risk assessments",
    "Your organisation has opted out of AI features",
];

const timeOf = (at: Date) => at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

// A function, so starting over stamps the greeting with the time it reappeared.
const openingTurns = (): Turn[] => [
    {
        role: "assistant",
        text: "Hi, I am SafetyNest AI and I am here to help you create a thorough risk assessment. Describe the risk assessment and I will help you along.",
        at: new Date(),
    },
];

export default function AiRiskAssessment() {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const transcriptEnd = useRef<HTMLDivElement>(null);

    const [conversationId, setConversationId] = useState<number | null>(null);
    const [turns, setTurns] = useState<Turn[]>(openingTurns);
    const [draft, setDraft] = useState("");
    const [progress, setProgress] = useState(0);
    const [title, setTitle] = useState<string | null>(null);
    const [summary, setSummary] = useState<string[]>([]);
    const [assessment, setAssessment] = useState<RiskAssessmentAiAssessmentDTO | null>(null);
    const [startOverOpen, setStartOverOpen] = useState(false);
    const [stage, setStage] = useState<"chat" | "review">("chat");
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const [createdTemplate, setCreatedTemplate] = useState<{ id: number; name: string } | null>(null);
    const [conversationEnded, setConversationEnded] = useState(false);

    const send = useSendRiskAssessmentAiMessage();
    const addTemplate = useAddTemplate();
    const allPeople = usePeople();
    const { selectedSite, selectedAccount } = useAppSelector((state) => state.account);
    const { isWorknestUser } = useAuth();
    const raBase = `${baseRoute}/${moduleUrls[moduleAreas.RISKASSESSMENTS]}`;

    useEffect(() => {
        transcriptEnd.current?.scrollIntoView({ behavior: "smooth" });
    }, [turns, send.isLoading]);

    // Drops the conversation id, so the next message starts a fresh one server-side.
    // The old conversation stays as an audit record.
    function startOver() {
        setStartOverOpen(false);
        setConversationEnded(false);
        setCreatedTemplate(null);
        setStage("chat");
        setConversationId(null);
        setTurns(openingTurns());
        setDraft("");
        setProgress(0);
        setTitle(null);
        setSummary([]);
        setAssessment(null);
    }

    function onCreateTemplate() {
        if (!assessment) return;

        const payload = buildAiTemplatePayload(assessment, {
            conversationId,
            siteExternalId: isWorknestUser
                ? null
                : selectedSite?.externalId ?? selectedAccount?.account?.externalId ?? null,
            allPeople: allPeople?.data ?? [],
        });

        addTemplate.mutate(payload, {
            onSuccess: (result: any) =>
                setCreatedTemplate({ id: result?.templateId ?? result?.id, name: assessment.title }),
            onError: (error: any) => {
                console.error(error);
                const validation = error?.errors && Object.values(error.errors).flat().join(" ");
                enqueueSnackbar(validation ?? error?.title ?? "Could not create the template.", { variant: "error" });
            },
        });
    }

    function onSend() {
        const message = draft.trim();
        if (!message || send.isLoading) return;

        setTurns((prev) => [...prev, { role: "user", text: message, at: new Date() }]);
        setDraft("");

        send.mutate(
            { conversationId, message },
            {
                onSuccess: (reply: RiskAssessmentAiReplyDTO) => {
                    setConversationId(reply.conversationId);
                    setProgress(reply.progress);
                    setTitle(reply.title);
                    setSummary(reply.summary ?? []);
                    setAssessment(reply.assessment);
                    setTurns((prev) => [...prev, { role: "assistant", text: reply.message, questions: reply.questions, at: new Date() }]);
                },
                onError: (error: any) => {
                    const reason = Array.isArray(error) ? error[0]?.message : error?.[0]?.message;
                    const ended = !!reason && terminalErrors.some((t) => reason.includes(t));

                    // A toast alone disappears; the transcript keeps the reason visible. A
                    // terminal limit also closes the composer, otherwise the message goes
                    // back in the box for another go.
                    setTurns((prev) => [
                        ...prev.slice(0, -1),
                        ...(ended ? [prev[prev.length - 1]] : []),
                        {
                            role: "system" as const,
                            text: reason ?? "Could not reach the assistant. Please try again.",
                            at: new Date(),
                        },
                    ]);

                    if (ended) setConversationEnded(true);
                    else setDraft(message);
                },
            }
        );
    }

    return (
        <Container maxWidth="xl">
            <CardBase
                title="AI-Assisted Assessment"
                description={
                    stage === "review"
                        ? "Review what the assistant has built, then create your template."
                        : "Answer a few questions and AI builds your full assessment for you — pre-filled and ready to review."
                }
            >
            {createdTemplate ? (
                <RiskAssessmentNextStepsCard
                    heading="Template created"
                    subtitle={`"${createdTemplate.name}" is saved to your templates. What would you like to do next?`}
                    note="You can create an assessment from this template at any time, for any site."
                    steps={[
                        {
                            label: "Create an assessment and complete it now",
                            onClick: () => history.push(`${raBase}/add/template?templateId=${createdTemplate.id}&completeNow=true`),
                        },
                        {
                            label: "Add it to a site to complete later",
                            onClick: () => history.push(`${raBase}/add/template?templateId=${createdTemplate.id}`),
                        },
                        { label: "Just keep the template", variant: "text", onClick: () => history.push(raBase) },
                    ]}
                />
            ) : stage === "review" && assessment ? (
                <AiAssessmentReview
                    assessment={assessment}
                    onChange={setAssessment}
                    onBack={() => setStage("chat")}
                    isSaving={addTemplate.isLoading}
                    onCreate={onCreateTemplate}
                />
            ) : (
                <>
            <div className={classes.layout}>
                <Paper className={classes.chatColumn}>
                    <div className={classes.chatHeader}>
                        <AutoAwesome color="secondary" />
                        <div>
                            <Typography variant="subtitle1">SafetyNest AI</Typography>
                            <Typography variant="caption" color="textSecondary">
                                Risk Assessment Builder
                            </Typography>
                        </div>
                        <div className={classes.chatHeaderSpacer} />
                        <Button
                            size="small"
                            color="primary"
                            startIcon={<RestartAlt />}
                            disabled={!conversationId || send.isLoading}
                            onClick={() => setStartOverOpen(true)}
                        >
                            Start over
                        </Button>
                    </div>
                    {send.isLoading && <LinearProgress className={classes.working} color="secondary" />}

                    <div className={classes.transcript}>
                        {turns.map((turn, index) =>
                            turn.role === "system" ? (
                                <div key={index} className={classes.systemRow}>
                                    <Typography variant="body2">{turn.text}</Typography>
                                    <Typography variant="caption" className={classes.time}>
                                        {timeOf(turn.at)}
                                    </Typography>
                                </div>
                            ) : turn.role === "assistant" ? (
                                <div key={index} className={classes.assistantRow}>
                                    <AutoAwesome color="secondary" fontSize="small" />
                                    <div>
                                        {turn.text && <Typography variant="body2">{turn.text}</Typography>}
                                        {turn.questions?.length ? (
                                            <ol className={classes.questionList}>
                                                {turn.questions.map((question, q) => (
                                                    <li key={q}>
                                                        <Typography variant="body2">{question}</Typography>
                                                    </li>
                                                ))}
                                            </ol>
                                        ) : null}
                                        <Typography variant="caption" className={classes.time}>
                                            {timeOf(turn.at)}
                                        </Typography>
                                    </div>
                                </div>
                            ) : (
                                <div key={index} className={classes.userRow}>
                                    <div>
                                        <Typography variant="body2" className={classes.userBubble}>
                                            {turn.text}
                                        </Typography>
                                        <Typography variant="caption" className={clsx(classes.time, classes.timeRight)}>
                                            {timeOf(turn.at)}
                                        </Typography>
                                    </div>
                                </div>
                            )
                        )}
                        <div ref={transcriptEnd} />
                    </div>

                    <div className={classes.composer}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            variant="outlined"
                            placeholder={
                                conversationEnded
                                    ? "This conversation has ended — use Start over to begin a new assessment."
                                    : send.isLoading
                                      ? "SafetyNest AI is working — this can take up to a minute..."
                                      : "Add your answer here..."
                            }
                            value={draft}
                            disabled={send.isLoading || conversationEnded}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    onSend();
                                }
                            }}
                        />
                        <div className={classes.composerActions}>
                            <Button
                                variant="contained"
                                color="primary"
                                endIcon={<Send />}
                                disabled={!draft.trim() || send.isLoading || conversationEnded}
                                onClick={onSend}
                            >
                                Send
                            </Button>
                        </div>
                        <Typography variant="caption" className={classes.disclaimer}>
                            <strong>Describe the work, not the people.</strong> Do not enter anyone's name, contact
                            details, date of birth or medical information. Refer to roles and groups instead —
                            "kitchen assistant", "expectant mothers", "contractors".
                        </Typography>
                        <Typography variant="caption" className={classes.disclaimer}>
                            SafetyNest AI can produce inaccurate or incomplete information. Review everything it
                            generates and confirm it with your competent person before relying on it.
                        </Typography>
                        <Typography variant="caption" className={classes.disclaimer}>
                            Usage is monitored to ensure fair use. AI assistance may be limited or withdrawn at any
                            time.
                        </Typography>
                    </div>
                </Paper>

                <div className={classes.sideColumn}>
                    <Paper className={classes.card}>
                        <div className={classes.progressLabel}>
                            <Typography variant="subtitle2">
                                {assessment ? "Ready to review" : "Gathering information"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {progress}%
                            </Typography>
                        </div>
                        <LinearProgress variant="determinate" value={progress} />
                    </Paper>

                    <Paper className={classes.card}>
                        <Typography variant="caption" color="textSecondary">
                            Assessment in progress
                        </Typography>
                        <Typography variant="subtitle1">{title ?? "Details come here"}</Typography>
                        {summary.length > 0 && (
                            <Typography variant="body2" className={classes.summaryItem}>
                                {summary.join(" • ")}
                            </Typography>
                        )}
                    </Paper>

                    {assessment && (
                        <Paper className={classes.card}>
                            <div className={classes.progressLabel}>
                                <Typography variant="subtitle2">{assessment.hazards.length} hazards</Typography>
                                <Button size="small" color="primary" startIcon={<Visibility />} onClick={() => setQuickViewOpen(true)}>
                                    Quick view
                                </Button>
                            </div>
                            {assessment.hazards.map((hazard, index) => (
                                <div key={index} className={classes.hazard}>
                                    <Typography variant="body2">{hazard.name}</Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        {hazard.controlsInPlace.length} controls in place,{" "}
                                        {hazard.additionalControls.length} additional
                                    </Typography>
                                </div>
                            ))}
                        </Paper>
                    )}
                </div>
            </div>

            <div className={classes.footer}>
                <Button variant="outlined" color="primary" startIcon={<ArrowBack />} onClick={() => history.push(`${raBase}/add`)}>
                    Back
                </Button>
                {assessment && (
                    <Button variant="contained" color="primary" endIcon={<ArrowForward />} onClick={() => setStage("review")}>
                        Continue
                    </Button>
                )}
            </div>
                </>
            )}
            </CardBase>

            <AiAssessmentQuickView
                assessment={assessment}
                open={quickViewOpen}
                onClose={() => setQuickViewOpen(false)}
            />

            <Dialog open={startOverOpen} onClose={() => setStartOverOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Start over?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This conversation and anything the assistant has built from it will be cleared, and you'll
                        begin a new assessment from scratch.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStartOverOpen(false)} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={startOver} color="error" variant="contained">
                        Start over
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
